/**
 * The selected language, and the document attributes that follow from it.
 *
 * `lang` and `dir` live on `<html>` rather than on a wrapper div, because they
 * govern more than layout: the browser's own hyphenation, quote marks, form
 * control direction, and the voice a screen reader uses all read the document
 * element. Setting them on a div leaves the scrollbar on the wrong side and the
 * page announced in the wrong language.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { COPY, directionFor, type Copy, type Language } from './translations.js';

interface LanguageValue {
  readonly language: Language;
  readonly direction: 'ltr' | 'rtl';
  readonly copy: Copy;
  readonly toggle: () => void;
}

const LanguageContext = createContext<LanguageValue | null>(null);

/** The merchant's last choice; a preference, so a full reload keeps it. */
const LANGUAGE_STORAGE_KEY = 'mouja:language';

function rememberedLanguage(fallback: Language): Language {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return stored === 'en' || stored === 'ar' ? stored : fallback;
  } catch {
    return fallback;
  }
}

export function LanguageProvider({
  children,
  initial = 'en',
}: {
  readonly children: ReactNode;
  readonly initial?: Language;
}): ReactNode {
  const [language, setLanguage] = useState<Language>(() => rememberedLanguage(initial));
  const direction = directionFor(language);
  const copy = COPY[language];

  useEffect(() => {
    const root = document.documentElement;
    root.lang = language;
    root.dir = direction;
    document.title = copy.documentTitle;

    const description = document.querySelector('meta[name="description"]');
    if (description) description.setAttribute('content', copy.documentDescription);
  }, [language, direction, copy]);

  const toggle = useCallback(() => {
    setLanguage((current) => {
      const next = current === 'en' ? 'ar' : 'en';
      try {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
      } catch {
        // Without storage the choice still applies for this visit.
      }
      return next;
    });
  }, []);

  const value = useMemo<LanguageValue>(
    () => ({ language, direction, copy, toggle }),
    [language, direction, copy, toggle],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageValue {
  const value = useContext(LanguageContext);
  if (value === null) {
    throw new Error('useLanguage must be used inside a LanguageProvider.');
  }
  return value;
}
